/**
 * @sazeo/sdk — Notifications hooks
 *
 * TanStack Query wrappers for the Notifications / Inbox module.
 * All calls use the user JWT. No service_role.
 *
 * Usage:
 *   const { data } = useNotifications(supabase, tenantId);
 *   const { data: count } = useUnreadCount(supabase, tenantId);
 */

import type { Notification } from '@sazeo/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

export type NotificationFilter = 'all' | 'unread' | 'mentions' | 'assignments';

export interface NotificationListOptions {
  filter?: NotificationFilter;
  cursor?: string | null;
  limit?: number;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const notificationsKeys = {
  all: ['notifications'] as const,
  list: (tenantId: string, opts?: NotificationListOptions) =>
    [...notificationsKeys.all, 'list', tenantId, opts ?? {}] as const,
  unreadCount: (tenantId: string) => [...notificationsKeys.all, 'unreadCount', tenantId] as const,
} as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return {} as T;
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Paginated notifications for the current user (unread-first). */
export function useNotifications(
  supabase: SupabaseClientLike,
  tenantId: string,
  opts: NotificationListOptions = {},
) {
  return useQuery({
    queryKey: notificationsKeys.list(tenantId, opts),
    queryFn: (): Promise<{ data: Notification[]; cursor: string | null }> => {
      const params = new URLSearchParams({
        limit: String(opts.limit ?? 50),
        ...(opts.cursor ? { cursor: opts.cursor } : {}),
        ...(opts.filter && opts.filter !== 'all' ? { filter: opts.filter } : {}),
      });
      return apiFetch(supabase, `notifications?${params.toString()}`);
    },
    enabled: Boolean(tenantId),
  });
}

/** Unread notification count — used for the inbox badge. */
export function useUnreadCount(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: notificationsKeys.unreadCount(tenantId),
    queryFn: (): Promise<{ count: number }> => apiFetch(supabase, 'notifications/unread-count'),
    enabled: Boolean(tenantId),
    // Poll every 30 s so the badge stays fresh even without realtime.
    refetchInterval: 30_000,
  });
}

/** Mark a single notification as read. */
export function useMarkRead(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) =>
      apiFetch<Notification>(supabase, `notifications/${notificationId}`, {
        method: 'PATCH',
        body: JSON.stringify({ read_at: new Date().toISOString() }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationsKeys.list(tenantId) });
      void qc.invalidateQueries({ queryKey: notificationsKeys.unreadCount(tenantId) });
    },
  });
}

/** Mark all notifications as read. */
export function useMarkAllRead(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ updated: number }>(supabase, 'notifications/mark-all-read', {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationsKeys.list(tenantId) });
      void qc.invalidateQueries({ queryKey: notificationsKeys.unreadCount(tenantId) });
    },
  });
}

/** Archive a notification (sets archived_at). */
export function useArchiveNotification(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) =>
      apiFetch<Notification>(supabase, `notifications/${notificationId}`, {
        method: 'PATCH',
        body: JSON.stringify({ archived_at: new Date().toISOString() }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationsKeys.list(tenantId) });
      void qc.invalidateQueries({ queryKey: notificationsKeys.unreadCount(tenantId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Notification preferences (migration 0023: notification_preferences table)
// ---------------------------------------------------------------------------

export interface NotificationPreferences {
  mentionsEmail: boolean;
  tasksEmail: boolean;
  documentsEmail: boolean;
  digestEmail: boolean;
  digestTime: string;
  quietHoursStart: string;
  quietHoursEnd: string;
}

/**
 * Fetch the current user's email notification preferences for a given tenant.
 * Calls GET /functions/v1/settings/me/notification-preferences.
 * Returns null when no row exists yet (defaults apply — all on except digest).
 */
export function useNotificationPreferences(
  supabase: SupabaseClientLike,
  userId: string | undefined,
  tenantId: string | undefined,
) {
  return useQuery({
    queryKey: ['notification-preferences', userId, tenantId],
    queryFn: (): Promise<NotificationPreferences | null> =>
      apiFetch<NotificationPreferences | null>(supabase, 'settings/me/notification-preferences'),
    enabled: Boolean(userId) && Boolean(tenantId),
  });
}

/**
 * Upsert the current user's notification preferences for the active tenant.
 * Calls PATCH /functions/v1/settings/me/notification-preferences.
 * Partial updates are supported — only supplied keys are written.
 */
export function useUpdateNotificationPreferences(
  supabase: SupabaseClientLike,
  userId: string | undefined,
  tenantId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prefs: Partial<NotificationPreferences>): Promise<NotificationPreferences> => {
      if (!userId || !tenantId)
        return Promise.reject(new Error('userId and tenantId are required'));
      return apiFetch<NotificationPreferences>(supabase, 'settings/me/notification-preferences', {
        method: 'PATCH',
        body: JSON.stringify(prefs),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notification-preferences', userId, tenantId] });
    },
  });
}
